import {
  ExpressionLogicalOperatorEnum,
  SubClause,
  WhereClause,
  WhereClauseCondition,
} from "../ars_ldm";
import {
  isSubClauseWithCompoundExpression,
  isWhereClauseWithCompoundExpression,
} from "../typeGuards";
import { parseConditionFromCode } from "./parseConditionFromCode";

/**
 * Token types produced by the where clause tokenizer.
 */
type WhereToken =
  | { type: "lparen" | "rparen" | "and" | "or" | "not" }
  | { type: "predicate"; value: string }
  | { type: "reference"; value: string };

/**
 * Internal parse tree used before materialising the ARS clause types.
 */
type ClauseNode =
  | { kind: "condition"; condition: WhereClauseCondition }
  | { kind: "compound"; logicalOperator: ExpressionLogicalOperatorEnum; whereClauses: ClauseNode[] }
  | { kind: "reference"; subClauseId: string };

/**
 * Checks whether a character should be treated as part of a word token.
 *
 * @param char - The character to check.
 * @returns True when the character is a word character.
 */
const isWordChar = (char: string): boolean => /[A-Za-z0-9_]/.test(char);

/**
 * Attempts to match a logical operator at the provided index.
 *
 * @param code - The full input string.
 * @param index - The current scan index.
 * @returns The matched operator or null if none is matched.
 */
const matchLogicalOperator = (
  code: string,
  index: number,
): ExpressionLogicalOperatorEnum | null => {
  const remaining = code.slice(index);
  const upper = remaining.toUpperCase();
  const operators = [
    ExpressionLogicalOperatorEnum.AND,
    ExpressionLogicalOperatorEnum.OR,
    ExpressionLogicalOperatorEnum.NOT,
  ];

  for (const operator of operators) {
    if (!upper.startsWith(operator)) {
      continue;
    }

    const before = index === 0 ? "" : code[index - 1];
    const after = code[index + operator.length];
    const beforeOk = before === "" || !isWordChar(before);
    const afterOk = after === "" || !isWordChar(after);

    if (beforeOk && afterOk) {
      return operator;
    }
  }

  return null;
};

/**
 * Tokenises a where clause string into logical operators, groupings, references, and predicates.
 * Predicates keep comparator and value text intact, including IN list parentheses.
 *
 * @param code - The raw where clause string.
 * @returns An array of tokens or null when lexing fails.
 */
const tokenizeWhereClause = (code: string): WhereToken[] | null => {
  const tokens: WhereToken[] = [];
  let buffer = "";
  let inQuote: "'" | '"' | null = null;
  let predicateParenDepth = 0;

  /**
   * Pushes the current buffer as a predicate token if it contains non-whitespace content.
   */
  const pushPredicate = (): void => {
    const trimmed = buffer.trim();
    if (trimmed) {
      tokens.push({ type: "predicate", value: trimmed });
    }
    buffer = "";
  };

  /**
   * Returns the type of the most recently emitted token, or null if no tokens have been emitted.
   *
   * @returns The last token type or null.
   */
  const lastTokenType = (): WhereToken["type"] | null =>
    tokens.length > 0 ? tokens[tokens.length - 1].type : null;

  /**
   * Determines whether a unary NOT operator can be emitted based on the last token type.
   *
   * @returns True if a NOT operator can start at the current position.
   */
  const canStartUnary = (): boolean => {
    const lastType = lastTokenType();
    return (
      lastType === null ||
      lastType === "and" ||
      lastType === "or" ||
      lastType === "not" ||
      lastType === "lparen"
    );
  };

  /**
   * Determines whether a binary AND/OR operator can be emitted based on the last token type.
   *
   * @returns True if an AND or OR operator can start at the current position.
   */
  const canStartBinary = (): boolean => {
    const lastType = lastTokenType();
    return lastType === "predicate" || lastType === "reference" || lastType === "rparen";
  };

  let i = 0;
  while (i < code.length) {
    const char = code[i];

    if (inQuote) {
      buffer += char;
      if (char === inQuote && code[i - 1] !== "\\") {
        inQuote = null;
      }
      i += 1;
      continue;
    }

    if (char === "'" || char === '"') {
      buffer += char;
      inQuote = char;
      i += 1;
      continue;
    }

    // Skip leading whitespace outside predicates
    if (char === " " && buffer.trim() === "" && predicateParenDepth === 0) {
      i += 1;
      continue;
    }

    if (char === "(" && buffer.trim() === "" && predicateParenDepth === 0) {
      tokens.push({ type: "lparen" });
      i += 1;
      continue;
    }

    if (char === ")" && predicateParenDepth === 0) {
      pushPredicate();
      tokens.push({ type: "rparen" });
      i += 1;
      continue;
    }

    if (char === "[" && buffer.trim() === "") {
      const closingIndex = code.indexOf("]", i + 1);
      /* istanbul ignore else - Defensive: malformed reference with no closing bracket */
      if (closingIndex > i + 1) {
        const referenceId = code.slice(i + 1, closingIndex).trim();
        /* istanbul ignore else - Defensive: empty reference ID like [] */
        if (referenceId) {
          tokens.push({ type: "reference", value: referenceId });
          i = closingIndex + 1;
          continue;
        }
      }
    }

    if (char === "(" && buffer.trim() !== "") {
      predicateParenDepth += 1;
      buffer += char;
      i += 1;
      continue;
    }

    if (char === ")" && predicateParenDepth > 0) {
      predicateParenDepth -= 1;
      buffer += char;
      i += 1;
      continue;
    }

    const operator = matchLogicalOperator(code, i);
    if (operator && predicateParenDepth === 0) {
      if (
        operator === ExpressionLogicalOperatorEnum.NOT &&
        buffer.trim() === "" &&
        canStartUnary()
      ) {
        tokens.push({ type: "not" });
        i += operator.length;
        continue;
      }

      if (operator !== ExpressionLogicalOperatorEnum.NOT) {
        if (buffer.trim() !== "") {
          pushPredicate();
          tokens.push({ type: operator === ExpressionLogicalOperatorEnum.AND ? "and" : "or" });
          i += operator.length;
          continue;
        }

        /* istanbul ignore else - Defensive: binary operator in invalid position */
        if (canStartBinary()) {
          tokens.push({ type: operator === ExpressionLogicalOperatorEnum.AND ? "and" : "or" });
          i += operator.length;
          continue;
        }
      }
    }

    buffer += char;
    i += 1;
  }

  if (inQuote || predicateParenDepth !== 0) {
    return null;
  }

  pushPredicate();

  return tokens.length > 0
    ? tokens
    : // istanbul ignore next - Defensive: ensures non-empty token stream
      null;
};

/**
 * Converts a parsed clause node into a sub-clause structure.
 *
 * @param node - The parsed clause node.
 * @returns The sub-clause or null if conversion fails.
 */
const buildSubClause = (node: ClauseNode): SubClause | null => {
  switch (node.kind) {
    case "condition":
      return {
        id: crypto.randomUUID(),
        subClauseId: "",
        condition: node.condition,
        level: 0,
        order: 0,
      };
    case "reference":
      return {
        id: crypto.randomUUID(),
        subClauseId: node.subClauseId,
        level: 0,
        order: 0,
      };
    case "compound": {
      const whereClauses = node.whereClauses
        .map((child) => buildSubClause(child))
        .filter((child): child is SubClause => child !== null);

      /* istanbul ignore next - Defensive: parser guarantees valid node types */
      if (whereClauses.length !== node.whereClauses.length) {
        return null;
      }

      return {
        id: crypto.randomUUID(),
        subClauseId: "",
        compoundExpression: {
          logicalOperator: node.logicalOperator,
          whereClauses,
        },
        level: 0,
        order: 0,
      };
    }
    /* istanbul ignore next - Defensive: TypeScript ensures exhaustive case coverage */
    default:
      return null;
  }
};

/**
 * Parses tokens into a clause tree using logical operator precedence.
 *
 * @param tokens - The token stream to parse.
 * @param availableVariables - List of available dataset.variable combinations for validation.
 * @param availableSubClauseIds - List of available subClauseIds for validation.
 * @returns The root clause node or null when parsing fails.
 */
const parseTokensToClauseNode = (
  tokens: WhereToken[],
  availableVariables: string[],
  availableSubClauseIds: string[],
): ClauseNode | null => {
  let index = 0;

  /**
   * Returns the current token without advancing the index.
   *
   * @returns The current token or null if at end of stream.
   */
  const peek = (): WhereToken | null => (index < tokens.length ? tokens[index] : null);
  /**
   * Returns the current token and advances the index to the next token.
   *
   * @returns The consumed token or null if at end of stream.
   */
  const consume = (): WhereToken | null =>
    index < tokens.length
      ? tokens[index++]
      : // istanbul ignore next - Defensive: handles end-of-stream gracefully
        null;

  /**
   * Parses a complete expression, starting with the lowest precedence (OR).
   *
   * @returns The parsed clause node or null if parsing fails.
   */
  const parseExpression = (): ClauseNode | null => parseOr();

  /**
   * Parses OR expressions (lowest precedence binary operator).
   * Accumulates multiple AND expressions separated by OR.
   *
   * @returns The parsed OR clause node or null if parsing fails.
   */
  const parseOr = (): ClauseNode | null => {
    const left = parseAnd();
    if (!left) {
      return null;
    }

    const clauses: ClauseNode[] = [left];
    while (peek()?.type === "or") {
      consume();
      const right = parseAnd();
      if (!right) {
        return null;
      }
      clauses.push(right);
    }

    return clauses.length === 1
      ? left
      : {
          kind: "compound",
          logicalOperator: ExpressionLogicalOperatorEnum.OR,
          whereClauses: clauses,
        };
  };

  /**
   * Parses AND expressions (higher precedence than OR).
   * Accumulates multiple NOT expressions separated by AND.
   *
   * @returns The parsed AND clause node or null if parsing fails.
   */
  const parseAnd = (): ClauseNode | null => {
    const left = parseNot();
    if (!left) {
      return null;
    }

    const clauses: ClauseNode[] = [left];
    while (peek()?.type === "and") {
      consume();
      const right = parseNot();
      if (!right) {
        return null;
      }
      clauses.push(right);
    }

    return clauses.length === 1
      ? left
      : {
          kind: "compound",
          logicalOperator: ExpressionLogicalOperatorEnum.AND,
          whereClauses: clauses,
        };
  };

  /**
   * Parses NOT expressions (highest precedence unary operator).
   * Recursively handles multiple consecutive NOTs.
   *
   * @returns The parsed NOT clause node or null if parsing fails.
   */
  const parseNot = (): ClauseNode | null => {
    if (peek()?.type === "not") {
      consume();
      const target = parseNot();
      if (!target) {
        return null;
      }

      return {
        kind: "compound",
        logicalOperator: ExpressionLogicalOperatorEnum.NOT,
        whereClauses: [target],
      };
    }

    return parsePrimary();
  };

  /**
   * Parses primary expressions (predicates, references, and parenthesized expressions).
   * This is the highest precedence level in the grammar.
   *
   * @returns The parsed primary clause node or null if parsing fails.
   */
  const parsePrimary = (): ClauseNode | null => {
    const token = peek();
    /* istanbul ignore next - Defensive: parser logic ensures tokens exist before calling parsePrimary */
    if (!token) {
      return null;
    }

    if (token.type === "lparen") {
      consume();
      const inner = parseExpression();
      if (!inner || peek()?.type !== "rparen") {
        return null;
      }
      consume();
      return inner;
    }

    if (token.type === "reference") {
      consume();
      if (!availableSubClauseIds.includes(token.value)) {
        return null;
      }
      return { kind: "reference", subClauseId: token.value };
    }

    if (token.type === "predicate") {
      consume();
      const condition = parseConditionFromCode(token.value, availableVariables);
      if (!condition) {
        return null;
      }
      return { kind: "condition", condition };
    }

    return null;
  };

  const result = parseExpression();
  if (!result || index !== tokens.length) {
    return null;
  }

  return result;
};

/**
 * Parses a where clause code string into a WhereClause object.
 * Handles simple conditions, compound expressions with logical operators, and brackets.
 * If no brackets are provided, uses standard operator precedence (NOT > AND > OR).
 *
 * @param code - The code string to parse (e.g., "ADSL.SAFFL = 'Y'" or "(ADSL.AGE > 18 AND ADSL.SEX = 'M')").
 * @param availableVariables - List of available dataset.variable combinations for validation.
 * @param availableSubClauseIds - List of available subClauseIds for validation.
 * @returns The parsed WhereClause or false if parsing fails.
 */
export const parseWhereClauseFromCode = (
  code: string,
  availableVariables: string[],
  availableSubClauseIds: string[] = [],
): WhereClause | false => {
  if (!code || code.trim() === "") {
    return false;
  }

  const tokens = tokenizeWhereClause(code);
  if (!tokens) {
    return false;
  }

  const rootNode = parseTokensToClauseNode(tokens, availableVariables, availableSubClauseIds);
  if (!rootNode) {
    return false;
  }

  if (rootNode.kind === "condition") {
    return {
      condition: rootNode.condition,
      level: 0,
      order: 0,
    };
  }

  if (rootNode.kind === "reference") {
    return false;
  }

  const whereClauses = rootNode.whereClauses
    .map((child) => buildSubClause(child))
    .filter((child): child is SubClause => child !== null);

  /* istanbul ignore next - Defensive: parser guarantees valid node types */
  if (whereClauses.length !== rootNode.whereClauses.length) {
    return false;
  }

  const whereClause: WhereClause = {
    compoundExpression: {
      logicalOperator: rootNode.logicalOperator,
      whereClauses,
    },
    level: 0,
    order: 0,
  };

  normalizeWhereClauseLevelsAndOrders(whereClause);
  return whereClause;
};

/**
 * Normalizes level and order values in a WhereClause hierarchy.
 * Sets level = parent level + 1 for child subclauses, and incrementing order values for siblings.
 *
 * @param whereClause - The WhereClause to normalize.
 * @param parentLevel - The level of the parent (default 0 for top-level).
 */
const normalizeWhereClauseLevelsAndOrders = (
  whereClause: WhereClause,
  parentLevel: number = 0,
): void => {
  whereClause.level = parentLevel;
  whereClause.order = 0;

  /* istanbul ignore else */
  if (
    isWhereClauseWithCompoundExpression(whereClause) &&
    whereClause.compoundExpression.whereClauses
  ) {
    const childLevel = parentLevel + 1;
    whereClause.compoundExpression.whereClauses.forEach((subClause, index) => {
      normalizeSubClauseLevelsAndOrders(subClause, childLevel, index);
    });
  }
};

/**
 * Normalizes level and order values in a SubClause hierarchy.
 * Sets level = parent level + 1 for child subclauses, and incrementing order values for siblings.
 *
 * @param subClause - The SubClause to normalize.
 * @param level - The level for this subclause.
 * @param order - The order for this subclause among its siblings.
 */
export const normalizeSubClauseLevelsAndOrders = (
  subClause: SubClause,
  level: number,
  order: number,
): void => {
  subClause.level = level;
  subClause.order = order;

  if (isSubClauseWithCompoundExpression(subClause) && subClause.compoundExpression.whereClauses) {
    const childLevel = level + 1;
    subClause.compoundExpression.whereClauses.forEach((child, index) => {
      normalizeSubClauseLevelsAndOrders(child, childLevel, index);
    });
  }
};
